import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../supabase/supabaseClient";

export type Notice = {
  id: number;
  title: string;
  content: string;
  created_at?: string;
  is_pinned?: boolean;
  is_popup?: boolean; // ✅ 팝업 여부 추가
};

type NoticeContextType = {
  notices: Notice[];
  loadNotices: () => Promise<void>;
  addNotice: (n: Omit<Notice, "id">) => Promise<void>;
  updateNotice: (n: Notice) => Promise<void>;
  deleteNotice: (id: number) => Promise<void>;
};

const NoticeContext = createContext<NoticeContextType | undefined>(undefined);

export function NoticeProvider({ children }: { children: React.ReactNode }) {
  const [notices, setNotices] = useState<Notice[]>([]);

  async function loadNotices() {
    const { data, error } = await supabase
      .from("notices")
      .select("*")
      .order("is_pinned", { ascending: false })
      .order("created_at", { ascending: false });
    if (error) console.error("❌ Error loading notices:", error);
    if (data) setNotices(data as Notice[]);
  }

  async function addNotice(notice: Omit<Notice, "id">) {
    const { error } = await supabase.from("notices").insert([notice]);
    if (error) throw error;
    await loadNotices();
  }

  async function updateNoticeItem(notice: Notice) {
    const { error } = await supabase
      .from("notices")
      .update({
        title: notice.title,
        content: notice.content,
        is_pinned: notice.is_pinned,
        is_popup: notice.is_popup, // ✅ 팝업 여부 포함
      })
      .eq("id", notice.id);
    if (error) throw error;
    await loadNotices();
  }

  async function deleteNoticeItem(id: number) {
    const { error } = await supabase.from("notices").delete().eq("id", id);
    if (error) throw error;
    await loadNotices();
  }

  useEffect(() => {
    loadNotices();
  }, []);

  return (
    <NoticeContext.Provider
      value={{
        notices,
        loadNotices,
        addNotice,
        updateNotice: updateNoticeItem,
        deleteNotice: deleteNoticeItem,
      }}
    >
      {children}
    </NoticeContext.Provider>
  );
}

export function useNotices() {
  const ctx = useContext(NoticeContext);
  if (!ctx) throw new Error("NoticeContext not found");
  return ctx;
}
